// dependencies
import Immutable, { isImmutable as checkIsImmutable, fromJS } from "immutable";
import { nanoid } from "nanoid/non-secure";
import React, { ReactNode, useMemo } from "react";
import { z } from "zod";

import { faCheck } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { InputWrapper, Text } from "@performyard/py-components";

import { OptionSchema } from "./Option";

// components
import { TextSize } from "../../../Text";
// css
import css from "./Checkboxes.module.scss";

export type Option = z.infer<typeof OptionSchema>;

interface ImmutableResult {
	id: string;
	label: string;
}

type PossiblyImmutableOptions = Immutable.List<ImmutableResult> | Option[];

interface IPossiblyImmutableProps extends Omit<BaseProps, "onChange" | "options" | "value"> {
	value?: PossiblyImmutableOptions;
	options: PossiblyImmutableOptions;
	onChange: (selectedOptions: PossiblyImmutableOptions) => void;
}

interface Info {
	title?: string;
	message: string;
	titleBottomLine?: boolean;
}

interface BaseProps {
	/**
	 * The label text of the input
	 */
	label?: string;
	/**
	 * The size of the label
	 */
	labelSize?: TextSize;
	/**
	 * Optional subtitle for the input
	 */
	subtitle?: string | ReactNode;
	/**
	 * Informational text to display under an info icon
	 */
	info?: Info;
	/**
	 * Any options that are selected
	 */
	value?: Option[];
	/**
	 * The options to display no matter what is selected.
	 */
	options: Option[];
	/**
	 * gap between the checkbox and label
	 */
	gap?: 1 | 2 | 3 | 4;
	/**
	 * The weight of the label
	 */
	labelWeight?: "regular" | "semi-bold" | "bold";
	/**
	 * The weight of the checkbox labels
	 */
	checkboxLabelWeight?: "regular" | "semi-bold" | "bold";
	/**
	 * a function is is called when the selected options change
	 */
	onChange: (selectedOptions: Option[]) => void;
	/**
	 * Any additional classes
	 * @default undefined
	 */
	className?: string;
	/**
	 * List of which checkboxes are disabled
	 */
	disabled?: string[];

	textSize?: TextSize;
}

/**
 * @deprecated use py-components CheckboxGroup instead
 * Checkboxes component allows selecting multiple options from a list of options.
 */
export const Checkboxes = (props: BaseProps) => {
	const {
		options,
		onChange,
		label,
		labelSize = "paragraph-3",
		labelWeight = "semi-bold",
		subtitle = null,
		value = [],
		className,
		gap = 1,
		checkboxLabelWeight = "regular",
		disabled = [],
		info,
		textSize,
	} = props;

	const handleOptionChange = (option: Option) => {
		let newSelectedOptions: Option[] = [];
		if (value.find((opt) => opt.value === option.value)) {
			newSelectedOptions = value.filter((op) => op.value !== option.value);
		} else {
			newSelectedOptions = [...value, option];
		}
		onChange(newSelectedOptions);
	};

	const isChecked = (option: Option) => value.some((opt) => opt.value === option.value);

	const inputID = useMemo(() => nanoid(), []);

	return (
		<InputWrapper label={label || ""} labelSize={labelSize} labelWeight={labelWeight} info={info} id={inputID}>
			{subtitle}
			<div
				className={`${css.checkboxes} ${className}`}
				data-testid="checkboxes"
				role={label ? "group" : undefined}
				aria-labelledby={label ? `label-${inputID}` : undefined}
			>
				{options.map((option) => {
					// generate a unique id to use for the input and label, avoid any conflict if multiple checkboxes on the same page have the same value
					const uniqueId = `${option.value}-${inputID}`;
					const isDisabled = disabled.includes(option.value);
					const conditionalStyle = { gap: `${gap * 8}px` };

					return (
						<label
							data-testid="checkbox-label"
							className={`${css.item} ${isDisabled ? css.disabled : null}`}
							style={conditionalStyle}
							key={option.value}
							htmlFor={uniqueId}
						>
							<input
								id={uniqueId}
								onChange={() => handleOptionChange(option)}
								type="checkbox"
								checked={isChecked(option)}
								style={{ display: "none" }}
								disabled={isDisabled}
							/>
							<div className={`${css.checkbox}`}>
								{isChecked(option) ? (
									<FontAwesomeIcon className={css.checkmark} icon={faCheck} data-testid="check" />
								) : null}
							</div>
							<Text size={textSize || "paragraph-3"} weight={checkboxLabelWeight}>
								{option.label}
							</Text>
						</label>
					);
				})}
			</div>
		</InputWrapper>
	);
};

const ImmutableWrapper = (props: IPossiblyImmutableProps) => {
	const value = useMemo(() => {
		if (checkIsImmutable(props.value)) {
			const immutableOptions = fromJS(props.value) as Immutable.List<ImmutableResult>;
			return immutableOptions.toJS().map((option: any) => ({
				value: option.value || option.name || option.id,
				label: option.label,
			})) as Option[];
		}
		return props.value as Option[];
	}, [props.value]);

	const options = useMemo(() => {
		if (checkIsImmutable(props.options)) {
			const immutableOptions = fromJS(props.options) as Immutable.List<ImmutableResult>;
			return immutableOptions
				.toJS()
				.map((option: any) => ({ value: option.value, label: option.label })) as Option[];
		}
		return props.options as Option[];
	}, [props.options]);

	// translate onChange to return immutable
	const onChange = (selectedOptions: Option[]) => {
		if (checkIsImmutable(props.options)) {
			const immutableOptions = selectedOptions.map((option) =>
				Immutable.Map({ value: option.value, label: option.label })
			);
			props.onChange(Immutable.List<any>(immutableOptions));
		} else {
			props.onChange(selectedOptions);
		}
	};

	return <Checkboxes {...props} onChange={onChange} value={value} options={options} />;
};

/** @deprecated use py-components CheckboxGroup instead */
export default ImmutableWrapper;
